import { PhotoSwipeLightbox } from 'vendor';
import { ProductLoader } from 'theme';

if (!customElements.get('relic-gallery')) {
  class RelicGallery extends HTMLElement {
    connectedCallback() {
      this.stage = this.querySelector('[data-relic-gallery-stage]');
      this.slidesContainer = this.querySelector('[data-relic-gallery-slides]');
      this.slides = Array.from(this.querySelectorAll('[data-relic-gallery-slide]'));
      this.thumbsContainer = this.querySelector('[data-relic-gallery-thumbs]');
      this.thumbs = Array.from(this.querySelectorAll('[data-relic-gallery-thumb]'));
      this.prevBtn = this.querySelector('[data-relic-gallery-prev]');
      this.nextBtn = this.querySelector('[data-relic-gallery-next]');
      this.viewInSpaceButton = this.querySelector('[data-shopify-xr]');
      this.currentIndex = 0;
      this.scrollSyncRaf = null;
      this.programmaticScrolling = false;
      this.scrollEndTimer = null;
      this._modelViewerUI = null;

      this.thumbs.forEach((thumb, i) => {
        thumb.addEventListener('click', () => {
          this.setActiveIndex(i, { scrollSlides: true });
        });
      });

      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => {
          const n = this.thumbs.length;
          if (n === 0) return;
          this.setActiveIndex((this.currentIndex - 1 + n) % n, { scrollSlides: true });
        });
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => {
          const n = this.thumbs.length;
          if (n === 0) return;
          this.setActiveIndex((this.currentIndex + 1) % n, { scrollSlides: true });
        });
      }

      this.slides.forEach((slide) => {
        const video = slide.querySelector('video-media');
        if (video) {
          video.tabIndex = 0;
          video.setAttribute('role', 'button');
          video.setAttribute('aria-label', 'Play video');
          video.addEventListener('keydown', (event) => {
            if (event.target !== video || !['Enter', ' '].includes(event.key)) return;
            event.preventDefault();
            video.play();
          });
          video.addEventListener('media:play', () => {
            video.removeAttribute('role');
            video.removeAttribute('tabindex');
            video.removeAttribute('aria-label');
          }, { once: true });
        }
        slide.addEventListener('click', () => {
          if (slide.querySelector('video-media')) return;
          this.openLightbox(this.currentIndex);
        });
      });

      if (this.slidesContainer) {
        this.slidesContainer.addEventListener('scroll', () => {
          if (this.programmaticScrolling) return;
          if (this.scrollSyncRaf) return;
          this.scrollSyncRaf = requestAnimationFrame(() => {
            this.scrollSyncRaf = null;
            if (this.programmaticScrolling) return;
            const idx = this.indexClosestToCenter();
            if (idx !== -1 && idx !== this.currentIndex) {
              this.setActiveIndex(idx, { scrollSlides: false });
            }
          });
        });
      }

      this.setupShopifyXr();
      this.syncXrButton(this.currentIndex);
    }

    setActiveIndex(i, { scrollSlides = false } = {}) {
      if (i < 0 || i >= this.thumbs.length) return;

      this.thumbs.forEach((t, j) => {
        t.classList.toggle('is-active', j === i);
        t.setAttribute('aria-selected', j === i ? 'true' : 'false');
      });
      this.slides.forEach((s, j) => {
        s.classList.toggle('is-active', j === i);
        if (j !== i) s.querySelector('video-media')?.pause();
      });

      if (scrollSlides && this.slidesContainer && this.slides[i]) {
        const left = this.slides[i].offsetLeft;
        if (Math.abs(this.slidesContainer.scrollLeft - left) > 1) {
          this.beginProgrammaticScroll(left);
        }
      }

      this.currentIndex = i;
      this.syncXrButton(i);

      const activeThumb = this.thumbs[i];
      if (activeThumb && (this.isCutOffBottom(activeThumb) || this.isCutOffTop(activeThumb))) {
        this.scrollThumbToCenter(activeThumb);
      }
    }

    syncXrButton(index) {
      if (!this.viewInSpaceButton) return;

      const slide = this.slides[index];
      if (slide?.getAttribute('data-media-type') === 'model') {
        this.viewInSpaceButton.setAttribute(
          'data-shopify-model3d-id',
          slide.getAttribute('data-media-id')
        );
        return;
      }

      const defaultId = this.viewInSpaceButton.getAttribute('data-shopify-model3d-default-id');
      if (defaultId) {
        this.viewInSpaceButton.setAttribute('data-shopify-model3d-id', defaultId);
      }
    }

    setupShopifyXr() {
      const handle = this.getAttribute('data-product-handle');
      if (!handle || !this.viewInSpaceButton || !window.Shopify?.loadFeatures) return;

      window.Shopify.loadFeatures([
        {
          name: 'shopify-xr',
          version: '1.0',
          onLoad: () => this.registerShopifyXrModels(handle),
        },
      ]);
    }

    async registerShopifyXrModels(handle) {
      if (!window.ShopifyXR) {
        document.addEventListener(
          'shopify_xr_initialized',
          () => this.registerShopifyXrModels(handle),
          { once: true }
        );
        return;
      }

      try {
        const product = await ProductLoader.load(handle);
        const models = (product?.media || []).filter((media) => media.media_type === 'model');
        if (models.length === 0) return;
        window.ShopifyXR.addModels(models);
        window.ShopifyXR.setupXRElements();
      } catch {
        // Draft products or network errors — AR button stays hidden via data-shopify-xr-hidden.
      }
    }

    beginProgrammaticScroll(left) {
      const container = this.slidesContainer;
      if (!container) return;
      this.programmaticScrolling = true;

      const clearFlag = () => {
        this.programmaticScrolling = false;
        container.removeEventListener('scrollend', clearFlag);
        if (this.scrollEndTimer) {
          clearTimeout(this.scrollEndTimer);
          this.scrollEndTimer = null;
        }
      };

      container.addEventListener('scrollend', clearFlag);
      if (this.scrollEndTimer) clearTimeout(this.scrollEndTimer);
      this.scrollEndTimer = setTimeout(clearFlag, 1500);

      container.scrollTo({ left, behavior: 'smooth' });
    }

    indexClosestToCenter() {
      if (!this.slidesContainer || this.slides.length === 0) return -1;
      const center = this.slidesContainer.scrollLeft + this.slidesContainer.clientWidth / 2;
      let bestIdx = -1;
      let bestDist = Infinity;
      this.slides.forEach((slide, j) => {
        const slideCenter = slide.offsetLeft + slide.offsetWidth / 2;
        const dist = Math.abs(slideCenter - center);
        if (dist < bestDist) {
          bestDist = dist;
          bestIdx = j;
        }
      });
      return bestIdx;
    }

    isHorizontal() {
      if (!this.thumbsContainer) return false;
      return getComputedStyle(this.thumbsContainer).flexDirection.startsWith('row');
    }

    isCutOffBottom(thumb) {
      const container = this.thumbsContainer;
      if (!container) return false;
      if (this.isHorizontal()) {
        const thumbEnd = thumb.offsetLeft + thumb.offsetWidth;
        const viewEnd = container.scrollLeft + container.clientWidth;
        return thumbEnd > viewEnd + 1;
      }
      const thumbEnd = thumb.offsetTop + thumb.offsetHeight;
      const viewEnd = container.scrollTop + container.clientHeight;
      return thumbEnd > viewEnd + 1;
    }

    isCutOffTop(thumb) {
      const container = this.thumbsContainer;
      if (!container) return false;
      if (this.isHorizontal()) {
        return thumb.offsetLeft < container.scrollLeft - 1;
      }
      return thumb.offsetTop < container.scrollTop - 1;
    }

    scrollThumbToCenter(thumb) {
      const container = this.thumbsContainer;
      if (!container) return;
      if (this.isHorizontal()) {
        const target = thumb.offsetLeft + thumb.offsetWidth / 2 - container.clientWidth / 2;
        container.scrollTo({ left: target, behavior: 'smooth' });
      } else {
        const target = thumb.offsetTop + thumb.offsetHeight / 2 - container.clientHeight / 2;
        container.scrollTo({ top: target, behavior: 'smooth' });
      }
    }

    get lightbox() {
      if (this._lightbox) return this._lightbox;
      this._lightbox = new PhotoSwipeLightbox({
        pswpModule: () => import('photoswipe'),
        bgOpacity: 0.85,
        maxZoomLevel: 3,
        arrowPrev: false,
        arrowNext: false,
        counter: false,
        close: false,
        zoom: false,
        mainClass: 'relic-pswp',
      });
      this._lightbox.on('uiRegister', () => this.registerLightboxUi());
      this._lightbox.on('change', () => this.setupActiveModelViewer());
      this._lightbox.on('close', () => {
        this.teardownModelViewerUI();
        this._lightbox.pswp?.currSlide?.content?.element?.querySelector('video-media')?.pause();
      });
      this._lightbox.on('contentDeactivate', ({ content }) => {
        content.element?.querySelector('video-media')?.pause();
      });
      this._lightbox.on('pointerDown', (e) => {
        const target = e.originalEvent?.target;
        if (target?.closest?.('[data-relic-pswp-model], model-viewer, video-media')) {
          e.preventDefault();
        }
      });
      this._lightbox.init();
      return this._lightbox;
    }

    registerLightboxUi() {
      const pswp = this._lightbox.pswp;

      pswp.ui.registerElement({
        name: 'close-button',
        className: 'circle-button circle-button--xl relic-pswp__close',
        ariaLabel: 'Close',
        order: 2,
        isButton: true,
        appendTo: 'wrapper',
        html: '<svg aria-hidden="true" focusable="false" fill="none" width="16" class="icon" viewBox="0 0 16 16"><path d="m1 1 14 14M1 15 15 1" stroke="currentColor" stroke-width="1"/></svg>',
        onClick: () => pswp.close(),
      });

      if (pswp.options.dataSource.length > 1) {
        pswp.ui.registerElement({
          name: 'previous-button',
          className: 'circle-button relic-pswp__prev',
          ariaLabel: 'Previous',
          order: 1,
          isButton: true,
          appendTo: 'wrapper',
          html: '<svg aria-hidden="true" focusable="false" fill="none" width="16" class="icon icon--direction-aware" viewBox="0 0 16 18"><path d="M11 1 3 9l8 8" stroke="currentColor" stroke-linecap="square"/></svg>',
          onClick: () => pswp.prev(),
        });
        pswp.ui.registerElement({
          name: 'next-button',
          className: 'circle-button relic-pswp__next',
          ariaLabel: 'Next',
          order: 3,
          isButton: true,
          appendTo: 'wrapper',
          html: '<svg aria-hidden="true" focusable="false" fill="none" width="16" class="icon icon--direction-aware" viewBox="0 0 16 18"><path d="m5 17 8-8-8-8" stroke="currentColor" stroke-linecap="square"/></svg>',
          onClick: () => pswp.next(),
        });
      }
    }

    slideToLightboxItem(slide) {
      const mediaType = slide.getAttribute('data-media-type') || 'image';
      const img = slide.querySelector('img');
      const width = parseInt(img?.getAttribute('width'), 10) || img?.naturalWidth || 1024;
      const height = parseInt(img?.getAttribute('height'), 10) || img?.naturalHeight || 1024;

      if (mediaType === 'video' || mediaType === 'external_video') {
        const template = slide.querySelector('[data-relic-video-content]');
        if (template) return { html: template.innerHTML, width, height, mediaType };
      }

      if (mediaType === 'model') {
        const template = slide.querySelector('[data-relic-model-content]');
        if (template) {
          return {
            html: template.innerHTML,
            width,
            height,
            mediaType: 'model',
            alt: img?.alt || '',
          };
        }
      }

      if (!img) {
        return {
          html: '<div class="relic-gallery__pswp-model" data-relic-pswp-model></div>',
          width: 1,
          height: 1,
          mediaType,
        };
      }

      return {
        src: img.src,
        srcset: img.srcset,
        msrc: img.currentSrc || img.src,
        width,
        height,
        alt: img.alt,
        mediaType,
      };
    }

    openLightbox(index) {
      const dataSource = this.slides.map((slide) => this.slideToLightboxItem(slide));
      if (dataSource.length === 0) return;
      this.lightbox.loadAndOpen(index, dataSource);
    }

    teardownModelViewerUI() {
      if (this._modelViewerUI) {
        try {
          this._modelViewerUI.pause();
        } catch {
          // ModelViewerUI may already be torn down with the slide DOM.
        }
        this._modelViewerUI = null;
      }
    }

    setupActiveModelViewer() {
      this.teardownModelViewerUI();

      const pswp = this._lightbox?.pswp;
      if (!pswp?.currSlide) return;

      const { data, content } = pswp.currSlide;
      if (data?.mediaType !== 'model') return;

      const root = content?.element;
      const modelViewer = root?.querySelector?.('model-viewer');
      if (!modelViewer || !window.Shopify?.loadFeatures) return;

      window.Shopify.loadFeatures([
        {
          name: 'model-viewer-ui',
          version: '1.0',
          onLoad: () => {
            if (!window.Shopify.ModelViewerUI) return;
            // Slide may have changed while the feature was loading.
            if (this._lightbox?.pswp?.currSlide?.data?.mediaType !== 'model') return;
            const activeViewer = this._lightbox.pswp.currSlide.content?.element?.querySelector('model-viewer');
            if (!activeViewer) return;
            this._modelViewerUI = new window.Shopify.ModelViewerUI(activeViewer, { focusOnPlay: true });
            this._modelViewerUI.play();
          },
        },
      ]);
    }
  }

  customElements.define('relic-gallery', RelicGallery);
}
